import '@app/App.css';
import { Button } from "@comps/ui/button";
import CompozzSpinner from "@comps/ui/CompozzSpinner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { DuplicateObjectRequest, ObjectDto, ObjectInfos } from "@dto/objects";
import { FullRightAccess, hasGlobalRightAccess, ObjectRightType } from "@dto/Users";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ObjectMenuItem, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";

interface ObjectMenuDuplicateItemProps {
  objectInfo: ObjectInfos;
  onObjectUpdate: (objectInfos: ObjectInfos) => void;
  closeMenu: () => void;
}

function ObjectMenuDuplicateItem({ objectInfo, onObjectUpdate, closeMenu }: ObjectMenuDuplicateItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const hasObjectFullRight = hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess);

  const objectUpdated = (objectInfos: ObjectInfos) => {
    setIsDialogOpen(false);
    closeMenu();
    onObjectUpdate(objectInfos);
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasObjectFullRight) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange} modal={true}>
      <DialogTrigger className={TW_ObjectMenuItem + ` ${objectInfo === null ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Copy className="ObjectMenuItemIcon" />{t("object.button.duplicate.text")}</DialogTrigger>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("object.dialog.duplicate.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.duplicate.description")}</DialogDescription>
        </DialogHeader>
        {objectInfo && <ObjectDuplicateForm initialObject={objectInfo.object} onObjectUpdate={objectUpdated} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ObjectDuplicateFormProps {
  initialObject: ObjectDto;
  onObjectUpdate: (objectInfos: ObjectInfos) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function ObjectDuplicateForm({ initialObject, onObjectUpdate, onCancel }: ObjectDuplicateFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    objectName: z.string().min(1, {
      message: t("object.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      objectName: initialObject.name + t("object.dialog.duplicate.suffix"),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace) {
      console.error("No workspace selected");
      return;
    }

    setLoadingData(true);

    const duplicateObject: DuplicateObjectRequest = {
      workspaceKey: workspace._key,
      objectKey: initialObject._key,
      objectName: values.objectName,
      objectOrder: undefined,
    }

    post<ObjectInfos>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${initialObject._key}/duplicate`, duplicateObject).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          onObjectUpdate(response as ObjectInfos);
        }
      }
    })
      .finally(() => {
        setLoadingData(false);
      })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {loadingData && <div className="flex justify-center items-center">
          <CompozzSpinner />
        </div>}
        {!loadingData && <>
          <FormField
            control={form.control}
            name="objectName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("object.dialog.duplicate.label")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("object.dialog.duplicate.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {error && <div className={TW_ErrorMessage}>{error}</div>}
          {!error && <div className={TW_ErrorMessage}></div>}
        </>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover} disabled={loadingData}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover} disabled={loadingData}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ObjectMenuDuplicateItem;
